import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  SimpleChanges,
  OnChanges,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { isEqual } from 'lodash-es';
import { NO_VALUE } from '@pipes';
import {
  ActiveErrorData,
  ActiveErrorsStore,
  GatewayDevice,
  GatewayStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteDeviceDetails,
  RemoteDeviceType,
} from '@store';
import { DeviceConnectionStatusValues, DeviceRoles } from '@vi-iot/client/index';
import { Observable, Subject, takeUntil } from 'rxjs';
import { lowBatteryThreshold, criticalBatteryThreshold } from '@common';
import { SUFFIX } from 'src/app/shared/common/units';
import { InstallationService } from 'src/app/+installations/services/installation.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  CloseEventDialog,
  CommissioningDeleteDeviceConfirmModalComponent,
} from '../../../../commissioning/commissioning-delete-device-confirm-modal/commissioning-delete-device-confirm-modal.component';
import { CommissioningService } from '../../../../commissioning/commissioning.service';
import { MatTooltip } from '@angular/material/tooltip';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-added-device-item',
  templateUrl: './added-device-item.component.html',
  styleUrls: ['./added-device-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddedDeviceItemComponent implements OnInit, OnChanges {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  @Input() device: RemoteDeviceDetails;
  @Input() devices: RemoteDeviceDetails[];

  connectionStatus = DeviceConnectionStatusValues;
  SUFFIX = SUFFIX;
  noValue = NO_VALUE;
  lowBatteryThreshold = lowBatteryThreshold;
  criticalBatteryThreshold = criticalBatteryThreshold;
  DeviceRoles = DeviceRoles;

  isSrcDeviceNotConnected: boolean;
  heatingCircuitName$: Observable<string>;
  isAnyFHTAssignedToRoom: boolean;
  isFHTChannel$: Observable<boolean>;
  deviceErrors$: Observable<ActiveErrorData[]>;
  hasAnyFHTDemandRelayMode$: Observable<boolean>;
  specificDeviceRole: string;
  canDeleteClimateSensor: boolean;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    public dialogRef: MatDialogRef<AddedDeviceItemComponent>,
    public installationService: InstallationService,
    public gatewayStore: GatewayStore,
    public activeErrorsStore: ActiveErrorsStore,
    public installationDetailsStore: InstallationDetailsStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private commissioningService: CommissioningService
  ) {}

  ngOnInit(): void {
    this.deviceErrors$ = this.activeErrorsStore.getActiveErrorsForSRCDevice(this.device.device.id);
    this.installationFeaturesStore
      .canDeleteClimateSensor(this.device.device.id)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((value) => {
        this.canDeleteClimateSensor = value;
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['device']) {
      const newDevice = changes['device'].currentValue;
      const oldDevice = changes['device'].previousValue;

      if (!isEqual(JSON.stringify(newDevice), JSON.stringify(oldDevice))) {
        this.updateDeviceProperties();
        this.cdr.detectChanges();
      }
    }

    if (changes['devices']) {
      const newDevices = changes['devices'].currentValue;
      const oldDevices = changes['devices'].previousValue;
      if (!isEqual(JSON.stringify(newDevices), JSON.stringify(oldDevices))) {
        this.setIsAnyFHTAssignedToCO2Room();
        this.cdr.detectChanges();
      }
    }
  }

  private setIsAnyFHTAssignedToCO2Room(): void {
    this.isAnyFHTAssignedToRoom = this.devices.some(
      (dev) => dev.room === this.device.room && dev.device.roles.includes(DeviceRoles.FhtChannel)
    );
  }

  private updateDeviceProperties(): void {
    if (!this.device) {
      return;
    }

    this.isSrcDeviceNotConnected = this.checkSrcDeviceNotConnected(this.device);
    this.heatingCircuitName$ = this.installationService.getCircuitName(this.device.heatingCircuit);
    this.specificDeviceRole = this.getSpecificDeviceRole(this.device.device.roles);
    this.isFHTChannel$ = this.gatewayStore.isDeviceOfType(
      this.device.device.id,
      RemoteDeviceType.FHT_CHANNEL
    );
    this.setIsAnyFHTAssignedToCO2Room();
  }

  private checkSrcDeviceNotConnected(device: RemoteDeviceDetails): boolean {
    return (
      device.connectionStatus === this.connectionStatus.paired ||
      device.connectionStatus === this.connectionStatus.unknown
    );
  }

  private getSpecificDeviceRole(deviceRoles: string[]): string {
    if (deviceRoles.includes(DeviceRoles.ClimateSensor)) {
      return DeviceRoles.ClimateSensor;
    }
    if (deviceRoles.includes(DeviceRoles.Co2Sensor)) {
      return DeviceRoles.Co2Sensor;
    }

    return DeviceRoles.Others;
  }

  onDeleteDeviceAttempt(device: GatewayDevice): void {
    const dialogRef = this.dialog.open(CommissioningDeleteDeviceConfirmModalComponent, {
      width: '715px',
      panelClass: 'commissioning-delete-device-confirm-dialog',
      data: {
        deviceId: device.id,
      },
    });

    dialogRef.afterClosed().subscribe((event: CloseEventDialog) => {
      if (event.confirmed) {
        this.commissioningService.deleteDevice(device);
        if (event.shouldDeactivateSRC) {
          this.commissioningService.deactivateSRC();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
